import React, { useContext, useEffect, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import KeywordBadges from "../../components/ATS/KeywordBadges";
import ComparisonView from "../../components/JobMatcher/ComparisonView";
import ImprovementModal from "../../components/JobMatcher/ImprovementModal";
import * as resumeService from "../../services/resumeService";
import * as aiService from "../../services/aiService";
import * as jobService from "../../services/jobService";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";

export default function JobMatcher() {
  const { credits, updateCredits } = useContext(AuthContext);
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState("");
  const [uploadedResume, setUploadedResume] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [isMatching, setIsMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [showImprovement, setShowImprovement] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await resumeService.getAllResumes(1, 100);
        if (mounted) setResumes(r.resumes || []);
      } catch {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const charCount = jobDescription.length;

  const handleResumeUpload = async (file) => {
    if (!file) return;

    if (!file.name.endsWith(".pdf") && !file.name.endsWith(".docx")) {
      toast.error("Please upload a PDF or DOCX file");
      return;
    }

    try {
      // Set uploaded resume as selected
      setUploadedResume(file);
      // We'll use a special ID for uploaded resume
      setSelectedResume(`uploaded:${file.name}`);
      toast.success("Resume ready for matching");
    } catch (err) {
      toast.error("Failed to process resume");
    }
  };

  const handleMatch = async () => {
    if (!selectedResume || !jobDescription.trim()) {
      toast.error("Please select resume and enter job description");
      return;
    }
    if (credits < 2) {
      toast.error("Insufficient credits (2 required)");
      return;
    }

    setIsMatching(true);
    try {
      let resumeId = selectedResume;

      // If it's an uploaded resume, analyze it first
      if (selectedResume.startsWith("uploaded:") && uploadedResume) {
        const uploadResult = await aiService.analyzeUploadedResume(
          uploadedResume
        );
        resumeId = uploadResult.resumeId;
      }

      const result = await aiService.matchJob(resumeId, jobDescription);
      setMatchResult(result.analysis || result);
      await updateCredits();
      toast.success("Match analysis complete!");

      // Fetch similar jobs using found keywords or JD keyword terms (basic)
      const kw = (
        result.analysis?.matchingKeywords ||
        result.matchingKeywords ||
        []
      )
        .slice(0, 3)
        .join(" ");
      if (kw) {
        const jobs = await jobService.searchJobs({
          keyword: kw,
          page: 1,
          limit: 5,
        });
        setSimilarJobs(jobs.jobs || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Matching failed");
    } finally {
      setIsMatching(false);
    }
  };

  const score = matchResult?.matchPercentage || 0;
  const barColor =
    score > 75 ? "bg-green-600" : score >= 50 ? "bg-yellow-500" : "bg-red-600";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-bold">Job Matcher</h1>
          <p className="text-gray-600">
            Match your resume with job descriptions
          </p>
        </header>

        <Card title="Enter Details">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Resume
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  className="flex-1 border rounded px-3 py-2 min-w-0"
                  value={selectedResume}
                  onChange={(e) => {
                    setSelectedResume(e.target.value);
                    if (
                      e.target.value &&
                      !e.target.value.startsWith("uploaded:")
                    ) {
                      setUploadedResume(null);
                    }
                  }}
                >
                  <option value="">Select a resume</option>
                  {resumes.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.title}
                    </option>
                  ))}
                  {uploadedResume && (
                    <option value={`uploaded:${uploadedResume.name}`}>
                      📄 {uploadedResume.name}
                    </option>
                  )}
                </select>
                <label
                  className="flex items-center justify-center px-3 py-2 border border-dashed border-purple-300 rounded hover:bg-purple-50 cursor-pointer flex-shrink-0"
                  title="Upload resume"
                >
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    className="hidden"
                    onChange={(e) => handleResumeUpload(e.target.files?.[0])}
                  />
                  <span className="text-purple-600 font-semibold text-sm">
                    📤
                  </span>
                </label>
              </div>
              {uploadedResume && (
                <p className="text-xs text-green-600 mt-1">✓ Ready</p>
              )}
            </div>
            <div className="md:col-span-1 lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Description
              </label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[120px] sm:min-h-[150px]"
                placeholder="Paste job description here..."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
              />
              <div className="text-xs text-gray-500 text-right mt-1">
                {charCount} chars
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <Button
              onClick={handleMatch}
              disabled={isMatching || credits < 2}
              className="w-full sm:w-auto"
            >
              {isMatching ? "Matching..." : "Find Matches"}
            </Button>
            {credits < 2 && (
              <span className="text-xs text-red-600">Requires 2 credits</span>
            )}
          </div>
        </Card>

        {isMatching && <LoadingSpinner />}

        {matchResult && !isMatching && (
          <Card title="Match Results">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="md:col-span-2 lg:col-span-1">
                <p className="text-sm text-gray-600">Overall Match</p>
                <p className="text-3xl font-bold">{score}%</p>
                <div className="h-2 bg-gray-200 rounded mt-2">
                  <div
                    className={`h-2 rounded ${barColor}`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>
              <div className="md:col-span-2 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="font-semibold text-sm">Matching Keywords</p>
                  <KeywordBadges
                    keywords={matchResult.matchingKeywords || []}
                    type="found"
                  />
                </div>
                <div>
                  <p className="font-semibold text-sm">Missing Keywords</p>
                  <KeywordBadges
                    keywords={matchResult.missingKeywords || []}
                    type="missing"
                  />
                </div>
                <div>
                  <p className="font-semibold">Matching Skills</p>
                  <KeywordBadges
                    keywords={matchResult.matchingSkills || []}
                    type="found"
                  />
                </div>
                <div>
                  <p className="font-semibold">Missing Skills</p>
                  <KeywordBadges
                    keywords={matchResult.missingSkills || []}
                    type="missing"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <p className="font-semibold">Experience Alignment</p>
              <p className="text-sm text-gray-700">
                {matchResult.experienceAlignment || ""}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ComparisonView
                resumeHighlights={(
                  matchResult.strengthAreas ||
                  matchResult.strengthAreas ||
                  []
                ).slice(0, 6)}
                jobRequirements={(
                  matchResult.gapAreas ||
                  matchResult.missingKeywords ||
                  []
                ).slice(0, 6)}
              />
            </div>
            <div className="mt-4">
              <p className="font-semibold">Improvement Suggestions</p>
              <ul className="list-disc ml-5 text-sm">
                {(matchResult.improvementSuggestions || []).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowImprovement(true)}
              >
                Apply Suggestions
              </Button>
              <Button
                variant="ghost"
                onClick={() => toast.info("Opening resume editor")}
              >
                Update Resume
              </Button>
              <Button
                variant="ghost"
                onClick={() => toast.info("Analysis saved")}
              >
                Save Analysis
              </Button>
            </div>
          </Card>
        )}

        {/* Similar jobs */}
        {similarJobs.length > 0 && (
          <Card title="Similar Job Postings">
            <ul className="divide-y">
              {similarJobs.map((j) => (
                <li key={j._id} className="py-2">
                  <p className="font-medium">{j.title}</p>
                  <p className="text-sm text-gray-600">{j.location}</p>
                </li>
              ))}
            </ul>
            <div className="mt-2">
              <Button
                variant="ghost"
                onClick={() => toast.info("Opening job search")}
              >
                Search Similar Jobs
              </Button>
            </div>
          </Card>
        )}
      </div>
      <ImprovementModal
        isOpen={showImprovement}
        onClose={() => setShowImprovement(false)}
        suggestions={matchResult?.improvementSuggestions || []}
        onApply={(items) =>
          toast.success(`${items.length} suggestions queued for apply`)
        }
      />
    </DashboardLayout>
  );
}
